import { useState } from 'react';
import IconAdd from '../../../components/icons/IconAdd';
import IconDelete from '../../../components/icons/IconDelete';
import MediaUploader from '../../../components/MediaUploader';
import RichTextEditor from '../../../components/RichTextEditor';
import { renderRichText } from '../../../utils/richText';
import { safeParseConfig } from '../blockConfig';
import { parseLength, formatLength } from '../../../variables/containerLayoutTypes';
import {
   MATCHING_DEFAULT_CONFIG, MATCHING_SIDE_KIND_LIST, MATCHING_SIDE_KIND_TEXT, MATCHING_MODE_LIST, getMatchingMode,
   normalizePairs, isSideFilled,
} from '../matchingConfig';

// A compact px/% length field, reusing ContainerConfigForm.jsx's exact
// length-input markup/CSS classes rather than a new widget - same
// convention, applied here to size media chips instead of container gaps.
function LengthField({ label, value, onChange }) {
   const { number, unit } = parseLength(value, 0);
   const setLength = (rawNumber, rawUnit) => onChange(formatLength(rawNumber === '' ? 0 : Number(rawNumber), rawUnit));
   return (
      <label className='exercise-block-matching-size-field'>
         {label}
         <div className='topic-container-length-input'>
            <input type='number' className='submit-field from-input txt-c' value={number}
               onChange={(e) => setLength(e.target.value, unit)} />
            <div className='topic-container-unit-toggle'>
               <span className={unit === 'px' ? 'active' : ''} onClick={() => setLength(number, 'px')}>px</span>
               <span className={unit === '%' ? 'active' : ''} onClick={() => setLength(number, '%')}>%</span>
            </div>
         </div>
      </label>
   );
}

// One side of one pair - a kind selector (text/image/audio/video) plus
// whatever input that kind needs. Media kinds always show a plain URL
// field (fallback for linking an external asset, same as MediaBlockEditor)
// with a collapsible MediaUploader underneath - showing the full
// library/upload/record widget open by default for every side of every
// pair would be overwhelming with more than 2-3 pairs, so only one picker
// is ever expanded at a time (tracked by the parent via `pickerKey`).
function MatchingSideEditor({ side, onChange, expanded, onToggleExpand, courseId, placeholder }) {
   const setKind = (kind) => onChange({ kind, value: '' });
   const setValue = (value) => onChange({ ...side, value });

   return (
      <div className='exercise-block-matching-side'>
         <div className='exercise-block-type-row exercise-block-matching-kind-row'>
            {MATCHING_SIDE_KIND_LIST.map((k) => (
               <div key={k.key} className={'exercise-block-type-chip exercise-block-matching-kind-chip' + (side.kind === k.key ? ' active' : '')}
                  onClick={() => setKind(k.key)}>
                  {k.label}
               </div>
            ))}
         </div>
         {side.kind === MATCHING_SIDE_KIND_TEXT ? (
            <RichTextEditor rows={1} value={side.value} placeholder={placeholder} onChange={setValue} />
         ) : (
            <div className='exercise-block-matching-media-field'>
               <input className='exercise-block-generic-text' value={side.value} placeholder='URL файлу'
                  onChange={(e) => setValue(e.target.value)} />
               {side.value ? (
                  side.kind === 'image' ? <img className='exercise-block-matching-media-thumb' src={side.value} alt='' /> :
                  side.kind === 'audio' ? <audio className='exercise-block-matching-media-thumb-audio' src={side.value} controls /> :
                  <video className='exercise-block-matching-media-thumb' src={side.value} controls />
               ) : ''}
               <div className='exercise-block-matching-picker-toggle' onClick={onToggleExpand}>
                  {expanded ? 'Сховати вибір файлу' : 'Обрати файл'}
               </div>
               {expanded ? <MediaUploader courseId={courseId} kind={side.kind} onUploaded={(url) => setValue(url)} /> : ''}
            </div>
         )}
      </div>
   );
}

export default function MatchingEditor({ block, courseId, onSave, onCancel }) {
   const initial = safeParseConfig(block.config, MATCHING_DEFAULT_CONFIG);
   const [pairs, setPairs] = useState(() => {
      const normalized = normalizePairs(initial.pairs);
      return normalized.length ? normalized : normalizePairs(MATCHING_DEFAULT_CONFIG.pairs);
   });
   const [mediaWidth, setMediaWidth] = useState(initial.mediaWidth || MATCHING_DEFAULT_CONFIG.mediaWidth);
   const [mediaHeight, setMediaHeight] = useState(initial.mediaHeight || MATCHING_DEFAULT_CONFIG.mediaHeight);
   const [mode, setMode] = useState(getMatchingMode(initial));
   const [expandedPicker, setExpandedPicker] = useState(null);

   const updateSide = (i, sideKey, next) => setPairs((prev) => prev.map((p, idx) => (idx === i ? { ...p, [sideKey]: next } : p)));
   const addPair = () => setPairs((prev) => [...prev, { left: { kind: MATCHING_SIDE_KIND_TEXT, value: '' }, right: { kind: MATCHING_SIDE_KIND_TEXT, value: '' } }]);
   const removePair = (i) => setPairs((prev) => prev.filter((_, idx) => idx !== i));

   const handleSave = () => {
      const cleanPairs = pairs.filter((p) => isSideFilled(p.left) || isSideFilled(p.right));
      onSave({ config: JSON.stringify({ mode, pairs: cleanPairs, mediaWidth, mediaHeight }) });
   };

   return (
      <div className='exercise-block-matching-editor'>
         <div className='exercise-block-type-row'>
            {MATCHING_MODE_LIST.map((m) => (
               <div key={m.key} className={'exercise-block-type-chip' + (mode === m.key ? ' active' : '')} onClick={() => setMode(m.key)}>{m.label}</div>
            ))}
         </div>
         <p className='exercise-block-hint-text'>{mode === 'deferred'
            ? 'Студент може з’єднати будь-що з будь-чим (і помилитися); правильність з’явиться лише після натискання «Перевірити».'
            : 'Правильне з’єднання фіксується одразу, неправильне лише блимає.'}</p>
         <p className='exercise-block-hint-text'>Кожну сторону пари можна зробити текстом, фото, аудіо або відео - будь-яке поєднання.</p>
         <div className='exercise-block-matching-size-row'>
            <LengthField label='Ширина медіа' value={mediaWidth} onChange={setMediaWidth} />
            <LengthField label='Висота медіа (фото/відео)' value={mediaHeight} onChange={setMediaHeight} />
         </div>
         {pairs.map((pair, i) => (
            <div className='exercise-block-matching-row' key={i}>
               <MatchingSideEditor side={pair.left} placeholder='Ліва частина' courseId={courseId}
                  onChange={(next) => updateSide(i, 'left', next)}
                  expanded={expandedPicker === i + '-left'}
                  onToggleExpand={() => setExpandedPicker((prev) => (prev === i + '-left' ? null : i + '-left'))} />
               <span className='exercise-block-matching-arrow'>↔</span>
               <MatchingSideEditor side={pair.right} placeholder='Права частина' courseId={courseId}
                  onChange={(next) => updateSide(i, 'right', next)}
                  expanded={expandedPicker === i + '-right'}
                  onToggleExpand={() => setExpandedPicker((prev) => (prev === i + '-right' ? null : i + '-right'))} />
               <div className='exercise-block-item-remove-btn' onClick={() => removePair(i)}><IconDelete style={{ fill: '#e0876c', width: 14, height: 14, margin: 0 }} /></div>
            </div>
         ))}
         <div className='exercise-block-item-add-btn' onClick={addPair}><IconAdd /> Додати пару</div>
         <div className='exercise-block-editor-actions'>
            <button type='button' className='exercise-block-save-btn' onClick={handleSave}>Зберегти</button>
            <button type='button' className='exercise-block-cancel-btn' onClick={onCancel}>Скасувати</button>
         </div>
      </div>
   );
}

function MatchingSidePreview({ side, mediaWidth, mediaHeight }) {
   if (side.kind === MATCHING_SIDE_KIND_TEXT) return <span>{renderRichText(side.value)}</span>;
   if (side.kind === 'image') return <img className='exercise-block-matching-preview-media' src={side.value} alt='' style={{ width: mediaWidth, height: mediaHeight }} />;
   if (side.kind === 'audio') return <audio className='exercise-block-matching-preview-media' src={side.value} controls style={{ width: mediaWidth }} />;
   return <video className='exercise-block-matching-preview-media' src={side.value} controls style={{ width: mediaWidth, height: mediaHeight }} />;
}

export function renderMatchingPreview(block) {
   const cfg = safeParseConfig(block.config, MATCHING_DEFAULT_CONFIG);
   const pairs = normalizePairs(cfg.pairs).filter((p) => isSideFilled(p.left) || isSideFilled(p.right));
   if (pairs.length === 0) return <span className='exercise-block-empty-hint'>Порожньо</span>;
   const mediaWidth = cfg.mediaWidth || MATCHING_DEFAULT_CONFIG.mediaWidth;
   const mediaHeight = cfg.mediaHeight || MATCHING_DEFAULT_CONFIG.mediaHeight;
   return (
      <div className='exercise-block-matching-preview'>
         {getMatchingMode(cfg) === 'deferred' ? <p className='exercise-block-hint-text'>Правильність — лише після «Перевірити»</p> : ''}
         {pairs.map((p, i) => (
            <div className='exercise-block-matching-preview-row' key={i}>
               <MatchingSidePreview side={p.left} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
               <span className='exercise-block-matching-arrow'>↔</span>
               <MatchingSidePreview side={p.right} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
            </div>
         ))}
      </div>
   );
}
